import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import React, { useId, useState } from 'react';

export interface AccordionEntry {
  question: string;
  answer: string;
  /** 分类标注，显示为条目右上的等宽小标签 */
  category?: string;
}

interface SxAccordionProps {
  items: AccordionEntry[];
  /** 默认展开的条目下标。参考站首条默认展开，降低交互门槛 */
  defaultOpen?: number | null;
}

/**
 * FAQ 手风琴。
 *
 * 无障碍要点：触发器是真正的 button（可 Tab 聚焦、空格/回车激活），
 * 通过 aria-expanded 暴露状态、aria-controls 关联答案区，
 * 答案区标记 role="region" 并回指触发器。加号图标是纯 CSS 伪元素，
 * 对屏幕阅读器不可见，避免重复播报。
 *
 * 单开模式：展开一项自动收起其他项，保持页面长度稳定。
 */
const SxAccordion: React.FC<SxAccordionProps> = ({ items, defaultOpen = 0 }) => {
  const [open, setOpen] = useState<number | null>(defaultOpen);
  const reduce = useReducedMotion();
  const uid = useId();

  return (
    <div>
      {items.map((item, i) => {
        const isOpen = open === i;
        const triggerId = `${uid}-trigger-${i}`;
        const panelId = `${uid}-panel-${i}`;

        return (
          <div key={item.question} className="sx-acc-item">
            <button
              id={triggerId}
              type="button"
              className="sx-acc-trigger"
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => setOpen(isOpen ? null : i)}
            >
              <span className="flex-1">{item.question}</span>
              {item.category && (
                <span className="sx-mono text-[9px] hidden md:block shrink-0">{item.category}</span>
              )}
              <span className="sx-acc-icon" aria-hidden="true" />
            </button>

            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  id={panelId}
                  role="region"
                  aria-labelledby={triggerId}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{
                    duration: reduce ? 0.01 : 0.34,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  style={{ overflow: 'hidden' }}
                >
                  <p className="sx-body pb-6 pr-8" style={{ maxWidth: '68ch' }}>
                    {item.answer}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

export default SxAccordion;
